// BarcodeDashboard.jsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, Input, Button } from './components/ui';
import { saveAs } from 'file-saver';
import ExcelJS from 'exceljs';
import './BarcodeDashboard.css';

const DEFAULT_PALETTE = ['#FFEEEE', '#EEFFEE', '#EEEEFF', '#FFFFEE', '#EEFFFF', '#FFEEFF'];

export default function BarcodeDashboard() {
  const [day, setDay] = useState('1 day');
  const [startOfRange, setStartOfRange] = useState('100-25-160001');
  const [totalQty, setTotalQty] = useState(160000);
  const [qtyPerRecord, setQtyPerRecord] = useState(200);
  const [stationLabels, setStationLabels] = useState('A,B,C,D,E,F');
  const [longData, setLongData] = useState([]);
  const [wideData, setWideData] = useState([]);
  const [columnsLong, setColumnsLong] = useState([]);
  const [columnsWide, setColumnsWide] = useState([]);
  const [colorMap, setColorMap] = useState({});
  const [showInstructions, setShowInstructions] = useState(false);

  const hexToArgb = (hex = DEFAULT_PALETTE[0]) => 'FF' + hex.replace('#', '');

  function generatePlan() {
    const m = startOfRange.match(/^(.*?)(\d+)$/);
    if (!m) return;
    const prefix = m[1];
    const numS = m[2];
    const numWidth = numS.length;
    let counter = parseInt(numS, 10);

    const stations = stationLabels.split(',').map(s => s.trim()).filter(Boolean);
    const n = stations.length;

    const fullPerStation = Math.floor(totalQty / (qtyPerRecord * n));
    let leftoverUnits = totalQty - fullPerStation * qtyPerRecord * n;
    const avg = leftoverUnits / n;
    const leftovers = Array(n).fill(Math.round(avg));
    const delta = leftovers.reduce((a, b) => a + b, 0) - leftoverUnits;
    leftovers[n - 1] -= delta;

    const longRows = [];
    stations.forEach((st, idx) => {
      const chunks = Array(fullPerStation).fill(qtyPerRecord);
      if (leftovers[idx] > 0) chunks.push(leftovers[idx]);

      chunks.forEach((q, j) => {
        const part = Math.floor(j / 5) + 1;
        const startSeq = counter;
        const endSeq = counter + q - 1;
        const startStr = `${prefix}${String(startSeq).padStart(numWidth, '0')}`;
        const endStr = `${prefix}${String(endSeq).padStart(numWidth, '0')}`;
        const refStart = `${st}${String(startSeq).padStart(numWidth, '0')}`;
        const refEnd = `${st}${String(endSeq).padStart(numWidth, '0')}`;

        longRows.push({
          Day: day,
          Part: part,
          'Start of Range': startStr,
          'End of Range': endStr,
          'Ref Start': refStart,
          'Ref End': refEnd,
          Qty: q,
          Station: st,
        });

        counter = endSeq + 1;
      });
    });

    const colsLong = Object.keys(longRows[0] || {});
    const grouped = {};
    stations.forEach(st => (grouped[st] = []));
    longRows.forEach(r => grouped[r.Station].push(r));
    const maxRows = Math.max(...Object.values(grouped).map(arr => arr.length));

    const wideRows = [];
    for (let i = 0; i < maxRows; i++) {
      const row = {};
      stations.forEach(st => {
        const rec = grouped[st][i] || {};
        colsLong.forEach(col => {
          row[`${col} (${st})`] = rec[col] || '';
        });
      });
      wideRows.push(row);
    }

    const colsWide = Object.keys(wideRows[0] || {});
    const cmap = {};
    stations.forEach((st, idx) => {
      cmap[st] = DEFAULT_PALETTE[idx % DEFAULT_PALETTE.length];
    });

    setLongData(longRows);
    setColumnsLong(colsLong);
    setWideData(wideRows);
    setColumnsWide(colsWide);
    setColorMap(cmap);
  }

  function downloadCSV() {
    if (!longData.length) return;
    const header = columnsLong.join(',') + '\n';
    const body = longData.map(r => columnsLong.map(c => r[c]).join(',')).join('\n');
    saveAs(new Blob([header + body], { type: 'text/csv;charset=utf-8;' }), '6_station_barcodes.csv');
  }

  async function downloadXLSX() {
    if (!longData.length) return;
    const workbook = new ExcelJS.Workbook();
    const wsLong = workbook.addWorksheet('Long');
    wsLong.addRow(columnsLong);
    longData.forEach(r => {
      const row = wsLong.addRow(columnsLong.map(c => r[c]));
      const fillColor = hexToArgb(colorMap[r.Station]);
      row.eachCell(cell => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fillColor } };
      });
    });

    const wsWide = workbook.addWorksheet('Wide');
    wsWide.addRow(columnsWide);
    wideData.forEach(r => {
      const row = wsWide.addRow(columnsWide.map(c => r[c]));
      Object.keys(colorMap).forEach((st, blockIdx) => {
        const fillColor = hexToArgb(colorMap[st]);
        const firstCol = blockIdx * columnsLong.length + 1;
        for (let offset = 0; offset < columnsLong.length; offset++) {
          row.getCell(firstCol + offset).fill = {
            type: 'pattern', pattern: 'solid', fgColor: { argb: fillColor }
          };
        }
      });
    });

    const buf = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), '6_station_barcodes.xlsx');
  }

  return (
    <div className="barcode-dashboard">
      <header className="bd-header">
        <div className="bd-header-left">
          <h1 className="bd-title">6-Station Barcode Planner</h1>
        </div>
        <Button onClick={() => setShowInstructions(true)} className="button--outline">
          Instructions
        </Button>
      </header>

      <main className="bd-main">
        <Card className="card--settings">
          <form className="form-grid" onSubmit={e => { e.preventDefault(); generatePlan(); }}>
            <div className="form-group">
              <label htmlFor="day">Day</label>
              <Input id="day" value={day} onChange={e => setDay(e.target.value)} />
            </div>
            <div className="form-group">
              <label htmlFor="startOfRange">Start of Range</label>
              <Input id="startOfRange" value={startOfRange} onChange={e => setStartOfRange(e.target.value)} />
            </div>
            <div className="form-group">
              <label htmlFor="totalQty">Total Qty</label>
              <Input id="totalQty" type="number" value={totalQty} onChange={e => setTotalQty(Number(e.target.value))} />
            </div>
            <div className="form-group">
              <label htmlFor="qtyPerRecord">Qty per Record</label>
              <Input id="qtyPerRecord" type="number" value={qtyPerRecord} onChange={e => setQtyPerRecord(Number(e.target.value))} />
            </div>
            <div className="form-group form-group--full">
              <label htmlFor="stationLabels">Station Labels</label>
              <Input id="stationLabels" value={stationLabels} onChange={e => setStationLabels(e.target.value)} />
            </div>
            <div className="form-actions">
              <Button type="submit" className="button--primary">
                Generate Plan
              </Button>
            </div>
          </form>
        </Card>

        {longData.length > 0 && (
          <>
            <div className="download-buttons">
              <Button onClick={downloadCSV} className="button--outline">
                Download CSV
              </Button>
              <Button onClick={downloadXLSX} className="button--outline">
                Download XLSX
              </Button>
            </div>

            <Card className="card--table">
              <h2>Long Format</h2>
              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>{columnsLong.map(col => <th key={col}>{col}</th>)}</tr>
                  </thead>
                  <tbody>
                    {longData.map((row, i) => (
                      <tr key={i} style={{ backgroundColor: colorMap[row.Station] }}>
                        {columnsLong.map(col => <td key={col}>{row[col]}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            <Card className="card--table">
              <h2>Wide Format</h2>
              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>{columnsWide.map(col => <th key={col}>{col}</th>)}</tr>
                  </thead>
                  <tbody>
                    {wideData.map((row, ri) => (
                      <tr key={ri}>
                        {columnsWide.map((col, ci) => {
                          const match = col.match(/\((.*?)\)$/);
                          const st = match ? match[1] : null;
                          const bg = st ? colorMap[st] : 'transparent';
                          return <td key={ci} style={{ backgroundColor: bg }}>{row[col]}</td>;
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </>
        )}
      </main>

      {showInstructions && (
        <div className="bd-modal-overlay" onClick={() => setShowInstructions(false)}>
          <div className="bd-modal-content" onClick={e => e.stopPropagation()}>
            <button className="bd-modal-close" onClick={() => setShowInstructions(false)}>×</button>
            <h2 className="bd-modal-title">How to Use</h2>
            <div className="bd-modal-body">
              <p><strong>1.</strong> Enter your target <em>Day</em> (e.g. <code>2025-05-02</code>).</p>
              <p><strong>2.</strong> Specify the <em>Start of Range</em> prefix + number.</p>
              <p><strong>3.</strong> Set your <em>Total Qty</em> and <em>Qty per Record</em>.</p>
              <p><strong>4.</strong> List your <em>Station Labels</em>, comma-separated.</p>
              <p><strong>5.</strong> Click <em>Generate Plan</em> to build both Long and Wide tables.</p>
              <p><strong>6.</strong> Download as CSV or XLSX using the buttons below.</p>
            </div>
            <div className="bd-modal-footer">
              <Button onClick={() => setShowInstructions(false)} className="button--primary">
                Got it!
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
